import { useIncomingCalls } from '@hit/react-jssip';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useExtensionPermissions } from '@/api/use-extension-permissions';
import { useLeadName } from '@/api/use-sf-lead-phone-book';
import { callerName } from '@/utils/caller-name';
import {
  INCOMING_CALL_NOTIFICATION_TIMEOUT_MS,
  buildIncomingCallNotificationContent,
  closeIncomingCallNotification,
  listenForIncomingCallNotificationActions,
  routeIncomingCallNotificationAction,
  shouldShowIncomingCallNotification,
  showIncomingCallNotification,
} from '@/utils/incoming-call-notification';

export const useNotifyOnIncomingCall = () => {
  const [incomingCall] = useIncomingCalls();
  const { data: permissions } = useExtensionPermissions();
  const leadName = useLeadName(incomingCall?.from.number);
  const canReject = permissions?.rejectCalls ?? true;
  const { t } = useTranslation('call');

  const name =
    callerName(incomingCall?.from.name, leadName) || incomingCall?.from.number;
  const callId = incomingCall?.id;

  // Holds the call whose notification was taken down on purpose. The effect
  // below re-runs on every render (useIncomingCalls rebuilds the call object
  // each time), so without this the auto-dismiss would be undone by the next
  // render putting the notification straight back on screen.
  const dismissedCallIdRef = useRef<string>();

  useEffect(() => {
    if (
      !incomingCall ||
      dismissedCallIdRef.current === incomingCall.id ||
      !shouldShowIncomingCallNotification({
        hasIncomingCall: true,
        visibilityState: document.visibilityState,
      })
    ) {
      return;
    }

    void showIncomingCallNotification(
      buildIncomingCallNotificationContent({
        callId: incomingCall.id,
        title: t('Chamada recebida'),
        callerLabel: name || t('Desconhecido'),
        answerLabel: t('Atender'),
        rejectLabel: t('Rejeitar'),
        canReject,
      })
    );
  }, [incomingCall, name, canReject, t]);

  // Keyed on the call id alone so that a re-render never restarts the timer.
  useEffect(() => {
    if (!callId) return;

    const dismiss = () => {
      dismissedCallIdRef.current = callId;
      void closeIncomingCallNotification(callId);
    };

    // Nothing retires a notification shown with `requireInteraction`, so a ring
    // whose end this page never sees (a dropped SIP WebSocket, for instance)
    // would leave it on the desktop indefinitely.
    const timeout = setTimeout(dismiss, INCOMING_CALL_NOTIFICATION_TIMEOUT_MS);

    // Back on the tab: the in-app incoming-call screen takes over, so the
    // notification is only noise from here on. Deliberately not recorded as
    // dismissed — it should come back if the agent leaves the tab again while
    // the call is still ringing.
    const closeWhenVisible = () => {
      if (document.visibilityState === 'visible') {
        void closeIncomingCallNotification(callId);
      }
    };

    document.addEventListener('visibilitychange', closeWhenVisible);
    // Closing the tab tears down the phone, so the notification must not
    // outlive the page that could act on it.
    window.addEventListener('pagehide', dismiss);

    // Runs when the ring ends, whichever way it ended: answered here, answered
    // or cancelled on another extension of the ring group, rejected, or rung
    // out — all of them remove the incoming call from the phone store.
    return () => {
      clearTimeout(timeout);
      document.removeEventListener('visibilitychange', closeWhenVisible);
      window.removeEventListener('pagehide', dismiss);
      void closeIncomingCallNotification(callId);
    };
  }, [callId]);

  useEffect(
    () =>
      listenForIncomingCallNotificationActions((action) => {
        if (!incomingCall) return;

        routeIncomingCallNotificationAction(action, {
          canReject,
          onAnswer: () => incomingCall.answer(),
          onReject: () => incomingCall.reject(),
        });
      }),
    [incomingCall, canReject]
  );
};
